import { z, RefinementCtx } from "zod";

import {
  createRequiredMessage,
  HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
  DATE_RANGE_ERROR_MESSAGE,
} from "../message";
import { HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX } from "../patterns";

import { basicInfoSchema, priceLoanSchema, cashflowSchema, buildingCostSchema, validateStrictDate } from "./common";
import { getRegistrationLoanErrors } from "./registrationLoan";

// 日付バリデーション
const validateDateField = (
  yearMonth: string | Date | null | undefined,
  day: string | null | undefined,
  dayFieldPath: string[],
  ctx: RefinementCtx
) => {
  if (!yearMonth || yearMonth === null || !day || day === "") return;

  const validation = validateStrictDate(yearMonth, day);
  if (!validation.isValid && validation.message) {
    ctx.addIssue({
      code: "custom",
      path: dayFieldPath,
      message: validation.message,
    });
  }
};

// validateStrictDateをre-export
export { validateStrictDate };

// commonからのスキーマをre-export
export { basicInfoSchema, priceLoanSchema, cashflowSchema, buildingCostSchema };

// 物件登録（区分）フォームスキーマ
export const addNewPropertySchema = basicInfoSchema
  .merge(priceLoanSchema)
  .merge(cashflowSchema)
  .superRefine((data, ctx) => {
    Object.entries(getRegistrationLoanErrors(data)).forEach(([field, message]) => {
      ctx.addIssue({ code: "custom", path: [field], message });
    });
    // 各日付フィールドのバリデーション
    validateDateField(data.bought_at, data.bought_at_day, ["bought_at_day"], ctx);
    validateDateField(data.starts_at, data.starts_at_day, ["starts_at_day"], ctx);
    validateDateField(data.ends_at, data.ends_at_day, ["ends_at_day"], ctx);

    // 契約期間の論理チェック（開始日 < 終了日）
    if (data.starts_at && data.starts_at_day && data.ends_at && data.ends_at_day) {
      const startDate = new Date(data.starts_at);
      startDate.setDate(Number(data.starts_at_day));

      const endDate = new Date(data.ends_at);
      endDate.setDate(Number(data.ends_at_day));

      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate >= endDate) {
        ctx.addIssue({
          code: "custom",
          path: ["ends_at"],
          message: DATE_RANGE_ERROR_MESSAGE,
        });
      }
    }

    // security_deposit（更新料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (!data.security_deposit || data.security_deposit.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["security_deposit"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.security_deposit)) {
          ctx.addIssue({
            code: "custom",
            path: ["security_deposit"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // key_money（礼金）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (!data.key_money || data.key_money.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["key_money"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.key_money)) {
          ctx.addIssue({
            code: "custom",
            path: ["key_money"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_new_lease_contract（新規賃貸契約手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (
        !data.outsourcing_fee_rate_of_new_lease_contract ||
        data.outsourcing_fee_rate_of_new_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_new_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_new_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_new_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_updating_lease_contract（賃貸契約更新時手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (
        !data.outsourcing_fee_rate_of_updating_lease_contract ||
        data.outsourcing_fee_rate_of_updating_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_updating_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_updating_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_updating_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }
  });

// 物件登録（一棟）フォームスキーマ
export const addNewBuildingPropertySchema = basicInfoSchema
  .omit({ floor: true, occupied_area_m2: true })
  .merge(priceLoanSchema)
  .merge(
    cashflowSchema.pick({
      monthly_fee: true,
      other_monthly_income: true,
      starts_at: true,
      starts_at_day: true,
      ends_at: true,
      ends_at_day: true,

      type_of_the_income_of_the_beginning_of_contract: true,
      security_deposit: true,
      key_money: true,
      outsourcing_fee_rate_of_new_lease_contract: true,
      outsourcing_fee_rate_of_updating_lease_contract: true,
    })
  )
  .merge(buildingCostSchema)
  .superRefine((data, ctx) => {
    Object.entries(getRegistrationLoanErrors(data)).forEach(([field, message]) => {
      ctx.addIssue({ code: "custom", path: [field], message });
    });
    // 各日付フィールドのバリデーション
    validateDateField(data.bought_at, data.bought_at_day, ["bought_at_day"], ctx);
    validateDateField(data.starts_at, data.starts_at_day, ["starts_at_day"], ctx);
    validateDateField(data.ends_at, data.ends_at_day, ["ends_at_day"], ctx);

    // 契約期間の論理チェック（開始日 < 終了日）
    if (data.starts_at && data.starts_at_day && data.ends_at && data.ends_at_day) {
      const startDate = new Date(data.starts_at);
      startDate.setDate(Number(data.starts_at_day));

      const endDate = new Date(data.ends_at);
      endDate.setDate(Number(data.ends_at_day));

      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate >= endDate) {
        ctx.addIssue({
          code: "custom",
          path: ["ends_at"],
          message: DATE_RANGE_ERROR_MESSAGE,
        });
      }
    }
  });

// 統合スキーマ（検討物件用）
export const considerationPropertySchema = basicInfoSchema
  .omit({
    management_type: true,
    sales_status: true,
    property_image: true,
    asking_price: true,
  })
  .merge(priceLoanSchema)
  .merge(
    cashflowSchema.omit({
      asking_price: true,
    })
  )
  .superRefine((data, ctx) => {
    Object.entries(getRegistrationLoanErrors(data)).forEach(([field, message]) => {
      ctx.addIssue({ code: "custom", path: [field], message });
    });
    // 各日付フィールドのバリデーション
    validateDateField(data.bought_at, data.bought_at_day, ["bought_at_day"], ctx);
    validateDateField(data.starts_at, data.starts_at_day, ["starts_at_day"], ctx);
    validateDateField(data.ends_at, data.ends_at_day, ["ends_at_day"], ctx);

    // 契約期間の論理チェック（開始日 < 終了日）
    if (data.starts_at && data.starts_at_day && data.ends_at && data.ends_at_day) {
      const startDate = new Date(data.starts_at);
      startDate.setDate(Number(data.starts_at_day));

      const endDate = new Date(data.ends_at);
      endDate.setDate(Number(data.ends_at_day));

      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate >= endDate) {
        ctx.addIssue({
          code: "custom",
          path: ["ends_at"],
          message: DATE_RANGE_ERROR_MESSAGE,
        });
      }
    }

    // security_deposit（更新料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (!data.security_deposit || data.security_deposit.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["security_deposit"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.security_deposit)) {
          ctx.addIssue({
            code: "custom",
            path: ["security_deposit"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // key_money（礼金）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (!data.key_money || data.key_money.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["key_money"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.key_money)) {
          ctx.addIssue({
            code: "custom",
            path: ["key_money"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_new_lease_contract（新規賃貸契約手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (
        !data.outsourcing_fee_rate_of_new_lease_contract ||
        data.outsourcing_fee_rate_of_new_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_new_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_new_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_new_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_updating_lease_contract（賃貸契約更新時手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (
        !data.outsourcing_fee_rate_of_updating_lease_contract ||
        data.outsourcing_fee_rate_of_updating_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_updating_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_updating_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_updating_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }
  });

// 型定義
export type BasicInfoFormData = z.infer<typeof basicInfoSchema>;
export type PriceLoanFormData = z.infer<typeof priceLoanSchema>;
export type CashflowFormData = z.infer<typeof cashflowSchema>;
export type BuildingCostFormData = z.infer<typeof buildingCostSchema>;
export type AddNewPropertyFormData = z.infer<typeof addNewPropertySchema>;
export type AddNewBuildingPropertyFormData = z.infer<typeof addNewBuildingPropertySchema>;
export type ConsiderationPropertyFormData = z.infer<typeof considerationPropertySchema>;
